import { useState, useEffect, useCallback, useRef } from "react";
import { ref, onValue } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { database, functions } from "../firebaseConfig";
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import { calculateWebsiteAnalyzerCost } from '../config/creditPlans';
import { useResearchLicenseRef } from '../hooks/useResearchAccess';
import { recordCompanyRecord } from '../shared/recordIntelligenceLog';

const DEFAULT_WA_SETTINGS = {
  activeModules: ['webSystem'],
};

export default function useWebsiteAnalyzer({
  userId,
  handleEdit,
  displayRows,
  saveScrollPosition,
  restoreScrollPosition,
}) {
  // Letzte Sicherung vor dem Function-Call — die Dialoge sperren bereits.
  const websiteAnalyzerLicensed = useResearchLicenseRef('hasWebsiteAnalyzer');

  const [analyzerLoadingRows, setAnalyzerLoadingRows] = useState({});
  const [waSettings, setWaSettings] = useState(DEFAULT_WA_SETTINGS);
  const [bulkProgress, setBulkProgress] = useState(null);

  const displayRowsRef = useRef(displayRows);
  const waSettingsRef = useRef(waSettings);
  const bulkDismissTimerRef = useRef(null);
  useEffect(() => { displayRowsRef.current = displayRows; }, [displayRows]);
  useEffect(() => { waSettingsRef.current = waSettings; }, [waSettings]);
  useEffect(() => () => clearTimeout(bulkDismissTimerRef.current), []);

  useEffect(() => {
    if (!userId) return;
    const settingsRef = ref(database, `/World/${userId}/settings/websiteAnalyzerSettings`);
    const unsub = onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setWaSettings({
          activeModules: data.activeModules || DEFAULT_WA_SETTINGS.activeModules,
        });
      }
    });
    return unsub;
  }, [userId]);

  const handleAnalyzeClick = useCallback(async (index) => {
    if (!websiteAnalyzerLicensed.current) return;
    saveScrollPosition();
    const rowData = displayRowsRef.current[index];
    if (!rowData) { alert("No data for this entry."); return; }
    if (!rowData.lead_website) { alert("Website URL required for Website Analyzer."); return; }

    const currentSettings = waSettingsRef.current;
    const creditCost = calculateWebsiteAnalyzerCost(currentSettings.activeModules);

    setAnalyzerLoadingRows(prev => ({ ...prev, [rowData.id]: true }));
    try {
      const deductCreditsFn = httpsCallable(functions, 'deductCredits');
      try {
        await deductCreditsFn({
          action: 'website_analyzer',
          amount: creditCost,
          worldId: userId,
          metadata: { contactId: rowData.id, modules: currentSettings.activeModules },
        });
      } catch (creditError) {
        const msg = creditError?.message || '';
        if (msg.includes('Not enough credits') || creditError?.code === 'resource-exhausted') {
          alert(`Not enough credits for Website Analyzer (${creditCost} credits required).`);
          setAnalyzerLoadingRows(prev => { const n = { ...prev }; delete n[rowData.id]; return n; });
          return;
        }
        throw creditError;
      }

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.websiteAnalyzer(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: rowData.lead_website,
          activeModules: currentSettings.activeModules,
        }),
      });

      if (response.status === 429) {
        alert('Daily scan limit reached. The limit resets at midnight.');
        return;
      }
      if (!response.ok) {
        const errBody = await response.json().catch(() => null);
        throw new Error(errBody?.error || `Scan failed (${response.status})`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        try {
          const analyzedAt = result.data.analyzedAt || new Date().toISOString();
          const websiteAnalysis = { ...result.data, analyzedAt };
          await handleEdit(rowData.id, 'websiteAnalysis', websiteAnalysis);
          await handleEdit(rowData.id, 'website_analysis_at', analyzedAt);
          await recordCompanyRecord(userId, rowData.id, 'website', rowData, {
            ...rowData,
            websiteAnalysis,
            website_analysis_at: analyzedAt,
          });
        } catch (err) {
          console.error('[WA] Firestore update error:', err);
        }
      } else {
        throw new Error("No analysis data received");
      }
    } catch (error) {
      console.error("[WA] Error:", error);
      alert(`Website Analyzer error: ${error.message}`);
    } finally {
      setAnalyzerLoadingRows(prev => { const n = { ...prev }; delete n[rowData.id]; return n; });
      setTimeout(restoreScrollPosition, 50);
    }
  }, [userId, saveScrollPosition, restoreScrollPosition, handleEdit, websiteAnalyzerLicensed]);

  const handleStartBulkAnalyze = useCallback(async (contactIds, creditBalance) => {
    if (!websiteAnalyzerLicensed.current) return;
    const contacts = displayRowsRef.current
      .filter(row => row?.id && contactIds.includes(row.id))
      .filter(row => row.lead_website?.trim() && !row.websiteAnalysis);

    if (contacts.length === 0) {
      alert("None of the selected contacts have a website URL for analysis.");
      return;
    }

    const currentSettings = waSettingsRef.current;
    const costPerScan = calculateWebsiteAnalyzerCost(currentSettings.activeModules);
    const totalCost = costPerScan * contacts.length;

    if (typeof creditBalance === 'number' && creditBalance < totalCost) {
      const affordable = Math.floor(creditBalance / costPerScan);
      const proceed = window.confirm(
        `Low credits: You have ${creditBalance} credits (${costPerScan} per scan).\n` +
        `Only ${affordable} of ${contacts.length} websites can be scanned.\n\nContinue anyway?`
      );
      if (!proceed) return;
      contacts.splice(affordable);
    }

    setBulkProgress({ total: contacts.length, done: 0, failed: 0 });
    const deductCreditsFn = httpsCallable(functions, 'deductCredits');

    try {
      await deductCreditsFn({
        action: 'website_analyzer',
        amount: costPerScan * contacts.length,
        worldId: userId,
        metadata: { contactCount: contacts.length, modules: currentSettings.activeModules, bulk: true },
      });
    } catch (creditError) {
      const msg = creditError?.message || '';
      if (msg.includes('Not enough credits') || creditError?.code === 'resource-exhausted') {
        alert(`Not enough credits for bulk scan (${costPerScan * contacts.length} credits required).`);
        setBulkProgress(null);
        return;
      }
      throw creditError;
    }

    const BATCH_SIZE = 50;
    let done = 0;
    let failed = 0;

    for (let i = 0; i < contacts.length; i += BATCH_SIZE) {
      const batch = contacts.slice(i, i + BATCH_SIZE);
      const urls = batch.map(c => c.lead_website);

      try {
        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.websiteAnalyzer(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            urls,
            activeModules: currentSettings.activeModules,
          }),
        });

        if (!response.ok) {
          failed += batch.length;
          setBulkProgress({ total: contacts.length, done, failed });
          continue;
        }

        const batchResult = await response.json();
        if (batchResult.results && Array.isArray(batchResult.results) && batchResult.results.length > 0) {
          const matchedContactIds = new Set();
          for (const item of batchResult.results) {
            const contact = batch.find(c => {
              try {
                const cUrl = c.lead_website.replace(/\/+$/, '').toLowerCase();
                const itemUrl = (item.url || '').replace(/\/+$/, '').toLowerCase();
                if (cUrl === itemUrl) return true;
                const cHost = new URL(cUrl.startsWith('http') ? cUrl : `https://${cUrl}`).hostname;
                const iHost = new URL(itemUrl.startsWith('http') ? itemUrl : `https://${itemUrl}`).hostname;
                return cHost === iHost;
              } catch (_) { return false; }
            });

            if (contact && item.status === 'ok' && item.data) {
              matchedContactIds.add(contact.id);
              try {
                const analyzedAt = item.data.analyzedAt || new Date().toISOString();
                const websiteAnalysis = { ...item.data, analyzedAt };
                await handleEdit(contact.id, 'websiteAnalysis', websiteAnalysis);
                await handleEdit(contact.id, 'website_analysis_at', analyzedAt);
                await recordCompanyRecord(userId, contact.id, 'website', contact, {
                  ...contact,
                  websiteAnalysis,
                  website_analysis_at: analyzedAt,
                });
                done++;
              } catch (_) {
                failed++;
              }
            } else {
              failed++;
            }
            setBulkProgress({ total: contacts.length, done, failed });
          }
          const unmatchedCount = batch.filter(c => !matchedContactIds.has(c.id)).length;
          if (unmatchedCount > 0) {
            failed += unmatchedCount;
            setBulkProgress({ total: contacts.length, done, failed });
          }
        } else {
          failed += batch.length;
          setBulkProgress({ total: contacts.length, done, failed });
        }
      } catch (err) {
        console.error('[WA-BULK] Batch error:', err);
        failed += batch.length;
        setBulkProgress({ total: contacts.length, done, failed });
      }
    }

    setBulkProgress(prev => prev ? { ...prev, done, failed, finished: true } : null);
    bulkDismissTimerRef.current = setTimeout(() => setBulkProgress(null), 5000);
  }, [userId, handleEdit, websiteAnalyzerLicensed]);

  return {
    analyzerLoadingRows,
    waSettings,
    bulkProgress,
    handleAnalyzeClick,
    handleStartBulkAnalyze,
  };
}
